import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Sparkles, RotateCcw, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { t } from '../../lib/i18n';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { ShareActions } from './ShareActions';

const formatDate = (value, language) => (value
  ? new Date(value).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '');

export const SimulationResult = ({ result, language, onReset }) => (
  <div className="space-y-6 fade-in" data-testid="simulation-result">
    {result.afterSrc && result.beforeSrc ? (
      <Card className="bg-lux-panel/90 border-white/[0.08] overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-lg font-display text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[var(--lux-gold)]" />
              {language === 'fr' ? 'Avant / Apr\u00e8s' : 'Before / After'}
            </CardTitle>
            <div className="flex items-center gap-2">
              {result.saved && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--lux-amber)] bg-lux-gold/10 border border-lux-gold/20 px-2 py-0.5 rounded-full" data-testid="simulation-saved-badge">
                  <Clock className="w-3 h-3" /> {t('savedOn', language)} {formatDate(result.createdAt, language)}
                </span>
              )}
              <Button onClick={onReset} variant="ghost" size="sm" className="text-white/40 hover:text-white" data-testid="simulation-new-photo-button">
                <RotateCcw className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Nouvelle photo' : 'New photo'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-6">
          <BeforeAfterSlider beforeSrc={result.beforeSrc} afterSrc={result.afterSrc} language={language} />
          <ShareActions beforeSrc={result.beforeSrc} afterSrc={result.afterSrc} language={language} />
        </CardContent>
      </Card>
    ) : (
      <Card className="bg-lux-panel/90 border-white/[0.08]">
        <CardContent className="pt-6">
          <div className="max-w-sm mx-auto">
            {result.beforeSrc && <img src={result.beforeSrc} alt="Original" className="w-full rounded-xl border border-white/10" />}
            <p className="text-white/40 text-xs text-center mt-2">
              {language === 'fr' ? 'La simulation visuelle n\'a pas pu \u00eatre g\u00e9n\u00e9r\u00e9e. Consultez l\'analyse ci-dessous.' : 'Visual simulation could not be generated. See the analysis below.'}
            </p>
          </div>
        </CardContent>
      </Card>
    )}

    {result.analysis && (
      <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="simulation-analysis-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-display text-white">
            {language === 'fr' ? 'Analyse & Recommandations' : 'Analysis & Recommendations'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="ai-response prose prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.analysis}</ReactMarkdown>
          </div>
        </CardContent>
      </Card>
    )}

    <div className="flex justify-center">
      <Button onClick={onReset} data-testid="simulation-new-simulation-button" className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] font-semibold active:scale-[0.98]">
        <RotateCcw className="w-4 h-4 mr-2" />
        {language === 'fr' ? 'Nouvelle simulation' : 'New simulation'}
      </Button>
    </div>
  </div>
);
